import {
  Box,
  Stack,
  Tiles,
  Checkbox,
  FieldLabel,
  TextField,
} from 'braid-design-system';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { parseLabel } from '../parseLabel.tsx';
import type { FormFieldWithOptions } from '../types.ts';

interface MultiSelectProps {
  element: FormFieldWithOptions;
}

export const MultiSelect = ({ element }: MultiSelectProps) => {
  const { watch, setValue } = useFormContext();
  const otherFieldName = `${element.id}-other-text`;
  const otherText = watch(otherFieldName) || '';

  const label = useMemo(
    () => parseLabel(element.id, element.label, element.labelLinks),
    [element.id, element.label, element.labelLinks],
  );

  return (
    <Controller
      key={element.id}
      name={element.id}
      rules={{
        required: element.required ? 'This field is required' : false,
      }}
      render={({ field, fieldState }) => {
        const checkboxOptions =
          element.options?.map((option) => {
            const checked = field.value.includes(option.value);
            return (
              <Checkbox
                key={option.id}
                id={option.id}
                checked={checked}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newValue = [
                    ...field.value.filter((p: unknown) => p !== option.value),
                    ...(e.currentTarget.checked ? [option.value] : []),
                  ];
                  field.onChange(newValue);
                }}
                label={option.label}
                value={option.value}
                message={fieldState.error?.message}
                tone={fieldState.invalid ? 'critical' : undefined}
              >
                {option.image && (
                  <Box>
                    <img
                      src={option.image.src}
                      alt={option.image.alt}
                      style={{
                        width: option.image.width || 'auto',
                        height: option.image.height || 'auto',
                        maxWidth: '100%',
                      }}
                    />
                  </Box>
                )}
              </Checkbox>
            );
          }) || [];

        const otherCheckboxes = element.other
          ? [
              <Checkbox
                key={`${element.id}-other`}
                id={`${element.id}-other`}
                checked={field.value.includes('other')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newValue = [
                    ...field.value.filter((p: unknown) => p !== 'other'),
                    ...(e.currentTarget.checked ? ['other'] : []),
                  ];
                  field.onChange(newValue);
                  setValue(
                    otherFieldName,
                    e.currentTarget.checked ? otherText : '',
                  );
                  if (field.value === 'other') {
                    field.onChange('other');
                  }
                }}
                label="Other"
                message={fieldState.error?.message}
                tone={fieldState.invalid ? 'critical' : undefined}
              >
                <TextField
                  aria-labelledby={`${element.id}-other`}
                  name={otherFieldName}
                  value={otherText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = [
                      ...field.value.filter(
                        (p: unknown) => p !== e.target.value,
                      ),
                      ...(e.currentTarget.checked ? [e.target.value] : []),
                    ];
                    field.onChange(newValue);
                    setValue(otherFieldName, newValue);
                    if (field.value === 'other') {
                      field.onChange('other');
                    }
                  }}
                  onFocus={() => {
                    field.onChange('other');
                  }}
                  message={fieldState.error?.message}
                  tone={fieldState.invalid ? 'critical' : undefined}
                />
              </Checkbox>,
            ]
          : [];

        const allCheckboxes = [...checkboxOptions, ...otherCheckboxes];

        return (
          <>
            <FieldLabel
              htmlFor={element.id}
              id={element.id}
              label={label}
              secondaryLabel={element.secondaryLabel}
            />
            {element.optionsColumn ? (
              <Tiles columns={2} space="small">
                {allCheckboxes}
              </Tiles>
            ) : (
              <Stack space="small">{allCheckboxes}</Stack>
            )}
          </>
        );
      }}
    />
  );
};
